import { clampToForm, timeSeriesFormOf } from "../chart-type";
import type { WorkspaceChartTypeEnum } from "../spec-gaps";
import { maybeAppendAutogeneratedName } from "../common";
import type { RichConceptDefinitionDTO, WorkspaceChain } from "./type";

/**
 * The chain's *rules*, as opposed to its steps: what a chain becomes when the chart type changes under it and
 * what a freshly picked concept seeds. Both are answers to "which values are legal for this concept, in this
 * form", so both are {@link clampToForm} — see the note there for why that is one function and not three.
 *
 * They live outside the two builders because both builders need them: the filter chain seeds a concept the
 * same way the chart chain does, and a copy of the rule per builder is how this family grew in the first
 * place.
 */

/**
 * The two flags that decide how a chain's name is regenerated after every write. Passed as one bag because
 * they always travel together: a surface that hides the source select also has to keep the source out of the
 * name, or the generated name could never match what the user sees.
 */
export type ChainNameOptions = { autoGenerateName: boolean; ignoreSource?: boolean };

/**
 * The chain, re-clamped to a chart type — the effect of switching the chart type under a chain that is
 * already parameterized.
 *
 * Switching the type switches *which* parameters are on offer, because a line chart draws the concept's
 * history and the history's form is its own (see {@link timeSeriesFormOf}). So a value that was legal under
 * the previous type can outlive it: 3Y is a horizon the performance history has and its scalar does not, and
 * a line chart edited into a column would go on requesting a synthetic metric that does not exist. In the
 * other direction a `RELATIVE` column carried onto a line asks for something the history never declared, and
 * since the engine enforces only the source the answer comes back absolute and is presented as relative.
 *
 * The name is regenerated from the clamped values, so a chart that now asks for 1Y stops calling itself 3Y.
 */
export function chainForChartType(
	chain: WorkspaceChain,
	chartType: WorkspaceChartTypeEnum,
	opts: ChainNameOptions,
): WorkspaceChain {
	return maybeAppendAutogeneratedName(
		{ ...chain, ...clampToForm(chain, timeSeriesFormOf(chain.concept, chartType)) },
		opts,
	);
}

/**
 * A fresh chain for a newly picked concept: every parameter seeded to the first value the concept's form
 * allows, and everything the previous concept answered dropped.
 *
 * Seeding *is* clamping with nothing to keep — `clampToForm` falls back to the form's first allowed value, so
 * with no incoming parameters it picks the first of each. That is why this is not a rule of its own: without
 * it a metric would land with no time horizon, the payload guard off and the select that owns it showing
 * empty, which is the same failure the chart-type-change effect exists to prevent.
 *
 * Only a `NUMERIC` concept is parameterized by horizon, confidence and representation — the chain renders
 * none of those steps for a property — so a property is seeded from its source alone rather than from values
 * nothing on screen could show or change. And the form is the *chart type's*, so picking a metric while a line
 * chart is selected seeds a horizon the history has rather than one only its scalar has.
 *
 * The tail each builder appends is its own: the filter chain follows this with a default comparison operator,
 * which is the filter's question and not the concept's parameterization.
 */
export function chainForPickedConcept(
	picked: { concept?: RichConceptDefinitionDTO; fakeConceptId?: string },
	chartType: WorkspaceChartTypeEnum,
	opts: ChainNameOptions & { name?: string },
): WorkspaceChain {
	const { concept, fakeConceptId } = picked;
	const form = timeSeriesFormOf(concept, chartType);
	const parameterizedLikeAMetric = concept?.dataType === "NUMERIC";

	return maybeAppendAutogeneratedName(
		{
			name: opts.name,
			...clampToForm({}, parameterizedLikeAMetric ? form : { allowedSource: form.allowedSource }),
			availableTagsConcepts: concept?.availableTagsConcepts,
			fakeConceptId,
			concept,
		},
		opts,
	);
}
